import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { catchError, Observable, retry, throwError } from 'rxjs';
import { ScreeningRoundResponse } from '../models/responseModels/screeningRoundDetails';
import { ScreeningRoundsResponse } from '../models/responseModels/screeningRounds';
import { environment } from 'environments/environment';
import { RecruitmentStatisticsResponse } from '../models/responseModels/recruitmentStatistics';
import { screening as screeningRequest } from '../models/requestModels/screeningRequest';

const API_BASE_URL = environment.hrmsApiUrl;
const AI_INTERVIEW_URL_BASE = environment.aiScreeningInterviewManagementUrl;
const screening = `${API_BASE_URL}/screening`;
const ScreeningRounds = `${API_BASE_URL}/rounds`;
const AI_INTERVIEW_URL = AI_INTERVIEW_URL_BASE;

@Injectable({
  providedIn: 'root'
})

export class AIScreeningService {
  constructor(private http: HttpClient) { }

  getAiScreeningRounds(page: number = 1, search: string = '', status: string = ''): Observable<ScreeningRoundsResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('per_page', '5')
      .set('search', search)
      .set('status', status);

    return this.http.get<ScreeningRoundsResponse>(ScreeningRounds, { params });
  }

  getScreeningRoundDetails(roundId: string | number): Observable<ScreeningRoundResponse> {
    const url = `${ScreeningRounds}/${roundId}`;
    return this.http.get<ScreeningRoundResponse>(url);
  }

  getPublicScreeningRoundDetails(accessCode: string, roundId: string | number): Observable<ScreeningRoundResponse> {
    const url = `${screening}/${accessCode}/${roundId}/round`;
    return this.http.get<ScreeningRoundResponse>(url);
  }

  createScreeningRound(round: screeningRequest): Observable<any> {
    return this.http.post<any>(ScreeningRounds, round);
  }

  updateScreeningRound(id: string | number, round: any): Observable<any> {
    return this.http.put<any>(`${ScreeningRounds}/${id}`, round);
  }

  inviteCandidates(roundId: string | number, body: any): Observable<any> {
    const url = `${ScreeningRounds}/${roundId}/candidates/bulk`;
    return this.http.post<any>(url, body);
  }

  getRecruitmentStats(): Observable<RecruitmentStatisticsResponse> {
    const url = `${ScreeningRounds}/stats`;
    return this.http.get<RecruitmentStatisticsResponse>(url);
  }

  sendOTP(accessCode: string, screeningId: string | number, email: string, name: string, phone?: string): Observable<any> {
    const url = `${screening}/${accessCode}/${screeningId}/public-register`
    const body = {
      email: email,
      name: name,
      ...(phone && { phone: phone })
    };
    return this.http.post<any>(url, body);
  }

  resendOTP(accessCode: string, screeningId: string | number, email: string): Observable<any> {
    const url = `${screening}/${accessCode}/${screeningId}/request-otp`;
    const body = { email: email };
    return this.http.post<any>(url, body);
  }

  verifyOTP(accessCode: string, screeningId: string | number, email: string, otp: string): Observable<any> {
    const url = `${screening}/${accessCode}/${screeningId}/verify-otp`
    const body = {
      email: email,
      otp_code: otp
    };
    return this.http.post<any>(url, body);
  }

  startPracticeSession(roundId: string, language: string) {
    const url = `${environment.hrmsApiUrl}/ai/practice-session/${roundId}?language=${language}`;
    return this.http.post<any>(url, {});
  }

  startInterview(body: string) {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded'
    });

    return this.http
      .post<any>(
        `${AI_INTERVIEW_URL}/realtime-interview/session`,
        body,
        { headers }
      )
      .pipe(
        retry({ count: 2, delay: 1000 }),
        catchError(error => throwError(() => error))
      );
  }

  getLanguages(): Observable<any> {
    const token = sessionStorage.getItem('ai_session_token');
    const headers = new HttpHeaders({
      ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    });
    return this.http.get<any>(`${AI_INTERVIEW_URL}/realtime-interview/languages`, { headers });
  }


  getInterviewReport(candidateId: number | string, sessionId: number | string, token?: string | null) {
    let params = new HttpParams()
      .set('front_end_session_id', sessionId ? sessionId.toString() : '')
      .set('candidate_id', candidateId ? candidateId.toString() : '');

    if (token) {
      params = params.set('token', token);
    }

    return this.http.get<any>(`${AI_INTERVIEW_URL}/interview/evaluation`, { params });
  }

  deleteScreeningRound(id: string | number): Observable<any> {
    const url = `${ScreeningRounds}/${id}`;
    return this.http.delete<any>(url);
  }

  shareReportViaEmail(payload: { emails: string[], report_url: string }): Observable<any> {
    const url = `${API_BASE_URL}/share-report`;
    return this.http.post<any>(url, payload);
  }

  uploadSessionResume(sessionId: string, file: File): Observable<any> {
    const url = `${screening}/session-resume`;
    const formData = new FormData();
    formData.append('front_end_session_id', sessionId);
    formData.append('resume', file);

    return this.http.post<any>(url, formData);
  }
}

